import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useMediaQuery from 'hooks/useMediaQuery'
import notify from 'components/ui/notify'
import { getUserDetails, updateUser } from 'slices/authSlice'
import { usersService } from 'services'
import { Form, useForm } from 'components/controls/useForm'
import UserUpdatePassword from 'components/UserUpdatePassword'
import TWControls from 'components/controls/TWControls'
import { SingleImageUploader } from 'components/FileUploader'
import Layout from 'components/Layout'
import { KeyRound } from 'lucide-react'
import Popup from 'components/Popup'
import Button from 'components/Button'

const UserProfileEditor = () => {
	const dispatch = useDispatch()
	const isMobile = useMediaQuery('(max-width: 768px)')

	const { userInfo, isInitialized } = useSelector(state => state.userLogin)
	const userId = userInfo._id

	useEffect(() => {
		if (!isInitialized) {
			dispatch(getUserDetails(userInfo._id))
		}
	}, [dispatch, userInfo, isInitialized])

	const { values, setValues, handleInputChange } = useForm(userInfo)

	const [image, setImage] = useState(userInfo.image)
	const [openPopup, setOpenPopup] = useState(false)
	const [passwordError, setPasswordError] = useState('')

	const submitForm = async values => {
		await dispatch(
			updateUser({
				_id: userId,
				image: values.image,
				name: values.name,
				firstName: values.firstName,
				email: values.email,
				company: values.company,
				phone: values.phone,
				title: values.title,
				role: values.role,
				firstTime: values.firstTime,
				isCompleted: values.isCompleted,
				linkedin: values.linkedin,
				instagram: values.instagram,
				facebook: values.facebook,
				twitter: values.twitter,
			}),
		)

		notify({
			text: 'Profile updated',
			icon: 'success',
			button: 'Ok',
		})
	}

	const handleChangePasswordButton = values => {
		setOpenPopup(true)
	}

	const handleSubmit = e => {
		e.preventDefault()
		if (!openPopup) {
			submitForm(values)
		}
	}

	const uploadImage = async fileInfo => {
		const fileUrl = fileInfo?.originalUrl ?? fileInfo?.cdnUrl ?? ''
		if (!fileUrl) return

		setImage(fileUrl)
		const newValues = { ...values, image: fileUrl }
		setValues(newValues)
		submitForm(newValues)
	}

	const updatePassword = async (passwordData, resetForm) => {
		let data
		try {
			data = await usersService().updatePassword({
				oldPassword: passwordData.oldPassword,
				newPassword: passwordData.newPassword,
			})
		} catch (error) {
			// Report what actually failed rather than blaming the password. See
			// the matching handler in UserProfileCN.tsx.
			setPasswordError(
				error?.response?.data?.message || error?.message || 'Could not update password.',
			)
			return
		}

		if (data?.success) {
			notify({
				title: `Password Updated!`,
				text: `Your password was successfully updated`,
				icon: 'success',
				button: 'Ok',
			})

			resetForm()
			setOpenPopup(false)
		} else {
			setPasswordError('Could not update password. Please try again.')
		}
	}

	// Render mobile version without Layout wrapper (no secondary nav)
	if (isMobile) {
		return (
			<div className="p-4 max-w-full">
				<Form onSubmit={handleSubmit}>
					<h3 className="mt-2 mb-2 font-normal text-sm text-gray-500 pb-2">Personal Details</h3>
					<TWControls.Input
						placeholder="Full Name"
						size="small"
						name="name"
						label="Full Name"
						value={values.name}
						onChange={handleInputChange}
						fullWidth
					/>
					<TWControls.Input
						placeholder="First Name"
						size="small"
						name="firstName"
						label="First Name"
						value={values.firstName}
						onChange={handleInputChange}
						fullWidth
					/>
					<TWControls.Input
						placeholder="Email"
						size="small"
						name="email"
						label="Email"
						value={values.email}
						onChange={handleInputChange}
						fullWidth
					/>
					<TWControls.Input
						placeholder="Phone"
						size="small"
						name="phone"
						label="Phone"
						value={values.phone}
						onChange={handleInputChange}
						fullWidth
					/>

					<h3 className="mt-2 mb-2 font-normal text-sm text-gray-500 pb-2">Employment Details</h3>
					<div className="flex flex-row gap-2 mb-2">
						<TWControls.Input
							placeholder="Company"
							size="small"
							name="company"
							label="Company"
							value={values.company}
							onChange={handleInputChange}
							fullWidth
						/>
						<TWControls.Input
							placeholder="Title"
							size="small"
							name="title"
							label="Title"
							value={values.title}
							onChange={handleInputChange}
							fullWidth
						/>
					</div>

					<h3 className="mt-2 mb-2 font-normal text-sm text-gray-500 pb-2">Social Media</h3>
					<div className="flex flex-row gap-2 mb-2">
						<TWControls.Input
							placeholder="LinkedIn"
							size="small"
							name="linkedin"
							label="LinkedIn"
							value={values.linkedin}
							onChange={handleInputChange}
							fullWidth
						/>
						<TWControls.Input
							placeholder="Instagram"
							size="small"
							name="instagram"
							label="Instagram"
							value={values.instagram}
							onChange={handleInputChange}
							fullWidth
						/>
					</div>

					<div className="flex flex-col items-center justify-center mt-4 mb-4">
						<SingleImageUploader
							value={image}
							onChange={uploadImage}
							size={180}
							className="w-[180px]"
							description="Drag & drop or click to update your photo"
						/>
					</div>

					<div className="flex flex-row gap-4 mt-4">
						<Button isOutline onClick={() => handleChangePasswordButton(values)}>
							Change Password
						</Button>
						<Button size="small" type="submit">
							Update
						</Button>
					</div>
				</Form>
				<Popup title="Change Password" openPopup={openPopup} setOpenPopup={setOpenPopup} icon={<KeyRound className="w-5 h-5" />} description="Update your account password">
					<UserUpdatePassword
						updatePassword={updatePassword}
						externalError={passwordError}
						setExternalError={setPasswordError}
					/>
				</Popup>
			</div>
		)
	}

	// Desktop version with Layout
	return (
		<Layout>
			<Layout.Page>
				<Layout.Body>
					<div className="mx-auto">
						<Form onSubmit={handleSubmit}>
							<div className="flex flex-row items-start gap-8 mb-4">
								<div className="flex flex-col w-1/2">
									<h3 className="mt-2 mb-2 font-normal text-sm text-gray-500 pb-2">Personal Details</h3>
									<div className="flex gap-2 mb-2">
										<TWControls.Input
											placeholder="Full Name"
											size="small"
											name="name"
											label="Full Name"
											value={values.name}
											onChange={handleInputChange}
											fullWidth
										/>
										<TWControls.Input
											placeholder="First Name"
											size="small"
											name="firstName"
											label="First Name"
											value={values.firstName}
											onChange={handleInputChange}
											fullWidth
										/>
									</div>
									<TWControls.Input
										placeholder="Email"
										size="small"
										name="email"
										label="Email"
										value={values.email}
										onChange={handleInputChange}
										fullWidth
									/>
									<TWControls.Input
										placeholder="Phone"
										size="small"
										name="phone"
										label="Phone"
										value={values.phone}
										onChange={handleInputChange}
										fullWidth
									/>
								</div>
								<div className="flex flex-col items-center justify-center w-1/2 pt-8">
									<SingleImageUploader
										value={image}
										onChange={uploadImage}
										size={180}
										className="w-[180px]"
										description="Drag & drop or click to update your photo"
									/>
								</div>
							</div>

							<h3 className="mt-2 mb-2 font-normal text-sm text-gray-500 pb-2">Employment Details</h3>
							<div className="flex gap-2 mb-2">
								<TWControls.Input
									placeholder="Company"
									size="small"
									name="company"
									label="Company"
									value={values.company}
									onChange={handleInputChange}
									fullWidth
								/>
								<TWControls.Input
									placeholder="Title"
									size="small"
									name="title"
									label="Title"
									value={values.title}
									onChange={handleInputChange}
									fullWidth
								/>
								<TWControls.Input
									placeholder="Role"
									size="small"
									name="role"
									label="Role"
									value={values.role}
									onChange={handleInputChange}
									fullWidth
								/>
							</div>

							<h3 className="mt-2 mb-2 font-normal text-sm text-gray-500 pb-2">Social Media</h3>
							<div className="flex gap-2 mb-2">
								<TWControls.Input
									placeholder="LinkedIn"
									size="small"
									name="linkedin"
									label="LinkedIn"
									value={values.linkedin}
									onChange={handleInputChange}
									fullWidth
								/>
								<TWControls.Input
									placeholder="Instagram"
									size="small"
									name="instagram"
									label="Instagram"
									value={values.instagram}
									onChange={handleInputChange}
									fullWidth
								/>
								<TWControls.Input
									placeholder="Facebook"
									size="small"
									name="facebook"
									label="Facebook"
									value={values.facebook}
									onChange={handleInputChange}
									fullWidth
								/>
								<TWControls.Input
									placeholder="Twitter"
									size="small"
									name="twitter"
									label="X (Twitter)"
									value={values.twitter}
									onChange={handleInputChange}
									fullWidth
								/>
							</div>

							<div className="flex flex-row gap-4 p-2">
								<Button isOutline onClick={() => handleChangePasswordButton(values)}>
									Change Password
								</Button>
								<Button size="small" type="submit">
									Update
								</Button>
							</div>
						</Form>
					</div>
				</Layout.Body>
			</Layout.Page>
			<Popup title="Change Password" openPopup={openPopup} setOpenPopup={setOpenPopup} icon={<KeyRound className="w-5 h-5" />} description="Update your account password">
				<UserUpdatePassword
					updatePassword={updatePassword}
					externalError={passwordError}
					setExternalError={setPasswordError}
				/>
			</Popup>
		</Layout>
	)
}

export default UserProfileEditor
